import type { DSDCenterData, DSDCurrencyData, SplitBlock, VolumeMeasures, VolumeRow } from '../types'

// =============================================================================
// Business unit › Department › Category for the DSD Center page (schema v2).
// The Planning Center + Lab total splits three ways that sum to it: Planning
// Center (five disciplines, or the same total cut by two stages), Lab (Design /
// Printing / Milling) and the Unclassified remainder. These helpers resolve the
// payload into department groups the split and volume tables both render.
// =============================================================================

export const PC_VIEWS = ['Discipline', 'Stage'] as const
export type PcView = (typeof PC_VIEWS)[number]

export const UNCLASSIFIED = 'unclassified'

/** A line the tables show and the drill opens: a category (a Planning Center
 *  discipline, a Lab category or the Unclassified remainder) or a Planning
 *  Center stage. */
export interface LineRef {
  kind: 'category' | 'stage'
  id: string
  name: string
  /** The department id the line sits under. */
  department: string
}

/** One department of the split: its subtotal and the lines under it. The
 *  Unclassified remainder is a department with a single line and no children. */
export interface Group<T> {
  id: string
  name: string
  /** Glossary term for the department row's InfoPopover (Lab). */
  info?: string
  /** Planning Center: the department whose lines follow the Discipline | Stage toggle. */
  stageable?: boolean
  subtotal: T
  children: { ref: LineRef; value: T }[]
  /** The department's category values whatever the view (Planning Center: the
   *  five disciplines even in the Stage view) — the subtotal is their sum. */
  leaves: T[]
  /** Set for a single-line department (Unclassified). */
  line?: LineRef
}

/** True when the payload carries the v2 classification on every currency. A v1
 *  bundle (rollback path) gets a notice instead of the category tables. */
export function hasV2(d: DSDCenterData): boolean {
  if (!d.categories?.length || !d.stages?.length || !d.labCategories) return false
  return d.currencies.every((c) => {
    const cur = d.data[c]
    return !!cur?.split.gross.byCategory && !!cur.split.gross.byStage && !!cur.split.gross.byLab && !!cur.volume.byCategory && !!cur.volume.byStage
  })
}

/** Department ids → display names, from the payload's volume.byDepartment rows. */
export function departmentNames(d: DSDCenterData): Record<string, string> {
  const names: Record<string, string> = {}
  for (const c of d.currencies) for (const r of d.data[c]?.volume.byDepartment ?? []) names[r.id] ??= r.name
  return names
}

const titleise = (id: string) => id.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase())

/** The departments of the split, in display order, with their line refs. */
export function lineRefs(d: DSDCenterData) {
  const names = departmentNames(d)
  const pcId = d.categories?.[0]?.department ?? 'planning_center'
  const labId = d.labCategories?.[0]?.department ?? 'lab'
  const name = (id: string) => names[id] ?? titleise(id)
  const disciplines: LineRef[] = (d.categories ?? []).map((c) => ({ kind: 'category', id: c.id, name: c.name, department: c.department }))
  const stages: LineRef[] = (d.stages ?? []).map((s) => ({ kind: 'stage', id: s.id, name: s.name, department: pcId }))
  const lab: LineRef[] = (d.labCategories ?? []).map((c) => ({ kind: 'category', id: c.id, name: c.name, department: c.department }))
  const unclassified: LineRef = { kind: 'category', id: UNCLASSIFIED, name: name(UNCLASSIFIED), department: UNCLASSIFIED }
  return {
    pc: { id: pcId, name: name(pcId), disciplines, stages },
    lab: { id: labId, name: name(labId), categories: lab },
    unclassified,
  }
}

/** Glossary term for a line's InfoPopover. */
export function lineTerm(ref: LineRef): string {
  if (ref.kind === 'stage') return 'stage'
  if (ref.id === UNCLASSIFIED) return 'unclassifiedRemainder'
  return `category:${ref.id}`
}

/** The monthly series behind one line of a Gross / PC credit / True Sale block. */
export function splitSeries(block: SplitBlock, ref: LineRef, labId: string): number[] {
  if (ref.kind === 'stage') return block.byStage?.[ref.id] ?? []
  if (ref.id === UNCLASSIFIED) return block.unclassified ?? []
  if (ref.department === labId) return block.byLab?.[ref.id] ?? []
  return block.byCategory?.[ref.id] ?? []
}

/** The volume row behind one line (category or stage), if the payload has it. */
export function volumeRow(cur: DSDCurrencyData, ref: LineRef): VolumeRow | undefined {
  const rows = ref.kind === 'stage' ? cur.volume.byStage : cur.volume.byCategory
  return rows?.find((r) => r.id === ref.id)
}

/** Assemble the department groups for one table. `valueOf` gives a line's
 *  windowed figures; subtotals are the sum of the department's categories, so
 *  the Planning Center subtotal is identical in the Discipline and Stage views
 *  (the stages are a second cut of the same total). */
export function buildGroups<T>(
  d: DSDCenterData,
  view: PcView,
  valueOf: (ref: LineRef) => T,
  add: (a: T, b: T) => T,
  zero: () => T,
): Group<T>[] {
  const { pc, lab, unclassified } = lineRefs(d)
  const sum = (vals: T[]) => vals.reduce((t, v) => add(t, v), zero())
  const pcLeaves = pc.disciplines.map(valueOf)
  const labLeaves = lab.categories.map(valueOf)
  const unclassifiedValue = valueOf(unclassified)
  const pcChildren = view === 'Stage' ? pc.stages.map((ref) => ({ ref, value: valueOf(ref) })) : pc.disciplines.map((ref, i) => ({ ref, value: pcLeaves[i] }))
  return [
    { id: pc.id, name: pc.name, stageable: true, subtotal: sum(pcLeaves), children: pcChildren, leaves: pcLeaves },
    { id: lab.id, name: lab.name, info: 'labBlock', subtotal: sum(labLeaves), children: lab.categories.map((ref, i) => ({ ref, value: labLeaves[i] })), leaves: labLeaves },
    { id: unclassified.id, name: unclassified.name, subtotal: unclassifiedValue, children: [], leaves: [unclassifiedValue], line: unclassified },
  ]
}

// --- Windowed figures --------------------------------------------------------

export interface SplitFig {
  gross: number
  pcCredit: number
  trueSale: number
}
export const splitZero = (): SplitFig => ({ gross: 0, pcCredit: 0, trueSale: 0 })
export const splitAdd = (a: SplitFig, b: SplitFig): SplitFig => ({ gross: a.gross + b.gross, pcCredit: a.pcCredit + b.pcCredit, trueSale: a.trueSale + b.trueSale })

/** Windowed paid-vs-included totals for one volume line. */
export type VolumeFig = { [K in keyof VolumeMeasures]: number }
export const volumeZero = (): VolumeFig => ({ paidUnits: 0, includedUnits: 0, paidCardInv: 0, paidPcCredit: 0, paidValue: 0 })
export const volumeAdd = (a: VolumeFig, b: VolumeFig): VolumeFig => ({
  paidUnits: a.paidUnits + b.paidUnits,
  includedUnits: a.includedUnits + b.includedUnits,
  paidCardInv: a.paidCardInv + b.paidCardInv,
  paidPcCredit: a.paidPcCredit + b.paidPcCredit,
  paidValue: a.paidValue + b.paidValue,
})
export function volumeFig(row: VolumeMeasures | undefined, sumWin: (a: number[]) => number): VolumeFig {
  if (!row) return volumeZero()
  return {
    paidUnits: sumWin(row.paidUnits),
    includedUnits: sumWin(row.includedUnits),
    paidCardInv: sumWin(row.paidCardInv),
    paidPcCredit: sumWin(row.paidPcCredit),
    paidValue: sumWin(row.paidValue),
  }
}
